import React, {useMemo} from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";
import {ComplyRateGauge} from "./TaskComplyRateStatic";

const LINE_COLORS = ["#3b82f6", "#f97316", "#22c55e", "#a855f7", "#ef4444"];

const calcGaugePercent = (row) => {
    if (!row) return 0;
    const denominator = Number(row.denominator) || 0;
    const cnt = Number(row.cnt) || 0;
    if (denominator === 0) return Number(row.cnt) || 0;
    return Math.round((cnt / denominator) * 100);
};

const toBarPoints = (raw) => {
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    return list.map((item) => ({
        name: String(item.name ?? ""),
        value: Number(item.cnt) || 0,
    }));
};

const toNivoSeriesList = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
};

const toMultiLinePoints = (seriesList) => {
    const list = toNivoSeriesList(seriesList);
    if (!list.length || !list[0]?.data?.length) return {points: [], keys: []};

    const keys = list.map((s) => String(s.id ?? ""));
    const points = list[0].data.map((point, index) => {
        const row = {name: String(point.x ?? "")};
        list.forEach((series) => {
            const key = String(series.id ?? "");
            row[key] = Number(series.data?.[index]?.y) || 0;
        });
        return row;
    });

    return {points, keys};
};

const toSingleLinePoints = (seriesList) => {
    const list = toNivoSeriesList(seriesList);
    if (!list.length) return [];
    const first = list[0];
    if (first?.data) {
        return first.data.map((p) => ({
            name: String(p.x ?? ""),
            value: Number(p.y) || 0,
        }));
    }
    return [];
};

function SlaCard({title, loading, children, legend}) {
    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden border rounded-si bg-white p-3">
            <h3 className="shrink-0 truncate text-[16px] font-bold leading-tight text-[#333]">{title}</h3>
            <div className="relative mt-2 flex min-h-0 flex-1 flex-col">
                {loading && <ChartLoadingOverlay />}
                <div className="relative min-h-0 flex-1">{children}</div>
                {legend}
            </div>
        </div>
    );
}

function SlaChartFrame({children}) {
    return <div className="absolute inset-0">{children}</div>;
}

function SlaCompactLegend({keys}) {
    if (!keys.length) return null;
    return (
        <div className="mt-1 flex shrink-0 flex-wrap items-center justify-center gap-x-2 gap-y-0.5 px-1">
            {keys.map((key, index) => (
                <span key={key} className="inline-flex max-w-full items-center gap-1 text-[10px] text-[#666]">
                    <span
                        className="inline-block h-0.5 w-3 shrink-0 rounded"
                        style={{backgroundColor: LINE_COLORS[index % LINE_COLORS.length]}}
                    />
                    <span className="truncate">{key}</span>
                </span>
            ))}
        </div>
    );
}

function ChartTooltip({active, payload, label, suffix = ""}) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-[4px] border border-[rgba(0,0,0,0.08)] bg-white px-3 py-2 text-[12px] shadow-sm">
            <div className="font-medium text-[#333]">{label}</div>
            {payload.map((entry) => (
                <div key={entry.dataKey} className="text-[#666]">
                    {entry.name} : {Number(entry.value || 0).toLocaleString()}
                    {suffix}
                </div>
            ))}
        </div>
    );
}

export function SlaTotalRateCard({data, loading}) {
    const tiles = useMemo(() => {
        const list = Array.isArray(data) ? data : [];
        const pick = (index, fallbackName) => ({
            name: list[index]?.name ?? fallbackName,
            value: Number(list[index]?.cnt) || 0,
        });

        const provider = pick(0, "수행사");
        const client = pick(1, "고객사");
        const providerRecent = pick(2, "수행사");
        const clientRecent = pick(3, "고객사");

        return [
            {group: "누적점수", name: provider.name, value: provider.value},
            {group: "누적점수", name: client.name, value: client.value},
            {group: "누적점수(최근월)", name: providerRecent.name, value: providerRecent.value},
            {group: "누적점수(최근월)", name: clientRecent.name, value: clientRecent.value},
        ];
    }, [data]);

    return (
        <SlaCard title="SLA 종합 수행률" loading={loading}>
            <div className="grid h-full min-h-0 grid-cols-2 grid-rows-2 gap-2">
                {tiles.map((tile) => (
                    <div
                        key={`${tile.group}-${tile.name}`}
                        className="flex min-h-0 items-center justify-between gap-2 rounded-[6px] border border-[#e5e7eb] bg-[#f8fafc] px-2.5 py-1.5"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-[10px] leading-tight text-[#64748b]">
                                {tile.group}
                            </div>
                            <div className="truncate text-[12px] font-semibold leading-tight text-[#334155]">
                                {tile.name}
                            </div>
                        </div>
                        <span className="shrink-0 text-[22px] font-bold leading-none text-[#3b82f6]">
                            {tile.value}%
                        </span>
                    </div>
                ))}
            </div>
        </SlaCard>
    );
}

export function SlaDualGaugeCard({data, loading}) {
    const left = data?.left;
    const right = data?.right;

    return (
        <SlaCard title={data?.title ?? "누적평가점수"} loading={loading}>
            <div className="grid h-full min-h-0 grid-cols-2 gap-1">
                <div className="flex min-h-0 flex-col items-center justify-center overflow-hidden">
                    <ComplyRateGauge
                        value={calcGaugePercent(left)}
                        title={left?.name ?? "누적점수 수행사"}
                        centered
                        compact
                    />
                </div>
                <div className="flex min-h-0 flex-col items-center justify-center overflow-hidden">
                    <ComplyRateGauge
                        value={calcGaugePercent(right)}
                        title={right?.name ?? "누적점수 고객사"}
                        centered
                        compact
                    />
                </div>
            </div>
        </SlaCard>
    );
}

export function SlaMonthlyBarCard({data, loading, title = "SLA 종합수행률 월별 통계"}) {
    const points = useMemo(() => toBarPoints(data), [data]);

    return (
        <SlaCard title={data?.title ?? title} loading={loading}>
            <SlaChartFrame>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={points} margin={{top: 4, right: 4, left: -20, bottom: 0}}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{fontSize: 11, fill: "#666"}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fontSize: 11, fill: "#666"}} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip suffix="%" />} cursor={false} />
                    <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={32} />
                </BarChart>
            </ResponsiveContainer>
            </SlaChartFrame>
        </SlaCard>
    );
}

export function SlaLineLegendCard({data, loading, title}) {
    const {points, keys} = useMemo(() => toMultiLinePoints(data), [data]);

    return (
        <SlaCard
            title={data?.title ?? title}
            loading={loading}
            legend={<SlaCompactLegend keys={keys} />}
        >
            <SlaChartFrame>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={points} margin={{top: 4, right: 4, left: -20, bottom: 0}}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{fontSize: 11, fill: "#666"}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fontSize: 11, fill: "#666"}} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip suffix="%" />} />
                    {keys.map((key, index) => (
                        <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            name={key}
                            stroke={LINE_COLORS[index % LINE_COLORS.length]}
                            strokeWidth={2}
                            dot={{r: 2}}
                            activeDot={{r: 4}}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
            </SlaChartFrame>
        </SlaCard>
    );
}

export function SlaSingleLineCard({data, loading, title}) {
    const points = useMemo(() => toSingleLinePoints(data), [data]);
    const lineTitle = data?.title ?? title;

    return (
        <SlaCard title={lineTitle} loading={loading}>
            <SlaChartFrame>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={points} margin={{top: 4, right: 4, left: -20, bottom: 0}}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{fontSize: 11, fill: "#666"}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fontSize: 11, fill: "#666"}} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip suffix="%" />} />
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#f97316"
                        strokeWidth={2}
                        dot={{r: 2, fill: "#f97316"}}
                        activeDot={{r: 4}}
                    />
                </LineChart>
            </ResponsiveContainer>
            </SlaChartFrame>
        </SlaCard>
    );
}
